import React, { Component, ReactElement, ReactNode } from 'react';

import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import ErrorMessage from 'buyside/components/presentation/ErrorMessage/errorMessage';

import rollbar from 'assets/util/rollbar';

import { Maybe } from 'interfaces/graphql-typings';

import styles from './errorBoundaryStyles.scss';

interface IState {
  error: Maybe<Error>;
  reportSubmitted: boolean;
  retried: boolean;
}

interface IProps {
  pageName: string;
  children?: ReactNode;
}

export class ErrorBoundary extends Component<IProps, IState> {
  constructor(props: any) {
    super(props);
    this.state = { error: null, retried: false, reportSubmitted: false };
  }

  componentDidCatch(error: Error): void {
    if (
      error.message === 'Network error: Network request failed with status 200 - "OK"' ||
      error.message === 'Error: Response not successful: Received status code 401'
    ) {
      location.reload();
    } else {
      this.setState({ error });
    }
  }

  componentDidUpdate(): void {
    if (this.state.error && !this.state.retried) {
      setTimeout(() => {
        this.setState({ error: null, retried: true });
      }, 500);
    }
  }

  handleReportError = (): void => {
    rollbar.error(this.state.error, 'User Reported Error: ' + (this.state.error as Error).message, {
      pageName: this.props.pageName
    });
    this.setState({ reportSubmitted: true });
  };

  render(): ReactElement | ReactNode {
    const { error, reportSubmitted, retried } = this.state;

    if (error) {
      if (retried) {
        return (
          <div className={styles.errorArea}>
            <ErrorMessage title="Uh Oh...">
              There was a problem loading {this.props.pageName}. Please refresh the page. If the
              problem persists contact <a href="mailto:support@sumzero.com">support@sumzero.com</a>.
              <Button
                className={styles.reportErrorButton}
                disabled={reportSubmitted}
                onClick={this.handleReportError}
                size={BUTTON_SIZES.TINY}
                text={reportSubmitted ? 'Error Report Submitted' : 'Submit Error Report'}
                type={BUTTON_TYPES.DANGER}
              />
            </ErrorMessage>
          </div>
        );
      }

      return null;
    } else {
      return this.props.children;
    }
  }
}

export default ErrorBoundary;
